import { useAuthContext } from '../providers/AuthContext';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { defaultImage } from '../settings/config';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu as MenuIcon,
  X,
  Baby,
  CreditCard,
  Calendar,
  User,
  LogOut,
} from 'lucide-react';

const Menu: React.FC = () => {
  const location = useLocation();
  const { user } = useAuthContext();
  const [profileImage, setProfileImage] = useState(defaultImage);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (user && user.profile) {
      setProfileImage(user.profile.image_url || defaultImage);
    } else {
      setProfileImage(defaultImage);
    }
  }, [user]);

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.key]);

  if (!user) return null;

  const displayName = user.profile?.first_name
    ? `${user.profile.first_name} ${user.profile.last_name}`
    : user.user?.email || user.email;

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-xl bg-card hover:bg-muted border border-border transition-colors"
        aria-label="Open menu"
      >
        <MenuIcon className="w-5 h-5 text-foreground" />
      </button>

      {/* Overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-xs z-40 lg:hidden"
            onClick={() => setOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`
        fixed lg:sticky top-0 left-0 z-50 lg:z-auto
        w-64 h-screen bg-sidebar border-r border-sidebar-border
        flex flex-col overflow-y-auto
        transition-transform duration-300 ease-out lg:translate-x-0
        ${open ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Header */}
        <div className="px-5 py-5 border-b border-sidebar-border flex items-center justify-between">
          <Link to="/home" className="no-underline flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--accent))] flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
              <span className="text-white text-sm font-black leading-none">P</span>
            </div>
            <div className="flex items-baseline gap-0.5">
              <span className="text-base font-bold tracking-tight text-foreground">plan</span>
              <span className="text-base font-bold tracking-tight bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent))] bg-clip-text text-transparent">a</span>
              <span className="text-base font-bold tracking-tight text-foreground">play</span>
            </div>
          </Link>
          <button
            onClick={() => setOpen(false)}
            className="lg:hidden p-1 rounded-lg hover:bg-muted transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Profile info */}
        <div className="px-5 py-4 border-b border-sidebar-border">
          <div className="flex items-center gap-3">
            <img
              alt="Profile"
              src={profileImage}
              className="w-10 h-10 rounded-full object-cover ring-2 ring-border"
            />
            <p className="font-semibold text-sm text-sidebar-foreground truncate">{displayName}</p>
          </div>
        </div>

        {/* Profile nav */}
        <nav className="px-3 py-4 flex-1">
          <p className="px-3 mb-2 text-2xs font-semibold uppercase tracking-widest text-muted-foreground">Profile</p>
          <NavItem to="/children" label="Children" icon={Baby} />
          <NavItem to="/cards" label="Cards" icon={CreditCard} />
          <NavItem to="/calendar" label="Calendar" icon={Calendar} />
        </nav>

        {/* Settings nav */}
        <nav className="px-3 py-4 border-t border-sidebar-border">
          <p className="px-3 mb-2 text-2xs font-semibold uppercase tracking-widest text-muted-foreground">Settings</p>
          <NavItem to="/account" label="Account" icon={User} />
          <NavItem to="/logout" label="Sign Out" icon={LogOut} />
        </nav>
      </aside>
    </>
  );
};

function NavItem({ to, label, icon: Icon }: { to: string; label: string; icon: React.FC<{ className?: string }> }) {
  const location = useLocation();
  const active = location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <Link
      to={to}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium no-underline transition-all duration-200
        ${active
          ? 'bg-sidebar-accent/10 text-sidebar-accent shadow-soft-xs'
          : 'text-sidebar-foreground hover:bg-muted hover:text-foreground'
        }`}
    >
      <Icon className={`w-4.5 h-4.5 ${active ? 'text-sidebar-accent' : ''}`} />
      <span>{label}</span>
    </Link>
  );
}

export default Menu;
